import { useEffect, useRef } from 'react'
import { match } from '@st/pattern'
import { Button, CaretDownIcon, PlusIcon, TrashIcon } from '@st/theme'
import {
  at,
  initialValue,
  labelFor,
  properties,
  schemaType,
  valueOf,
  type Address,
  type Edit,
  type EditorState,
  type Node,
  type Schema,
  type Scope
} from './model'
import { standardControls, type Control, type Controls } from './controls'

type Context = { state: EditorState; send: (edit: Edit) => void; controls: Controls; scope: Scope }

function controlFor(schema: Schema, controls: Controls): Control | undefined {
  // A name is a hint to try a registered control, not permission to bypass
  // enum/format/primitive fallback when that name is unknown.
  if (schema.source_type && controls[schema.source_type]) return controls[schema.source_type]
  if (schema.enum) return standardControls.enum
  if (schema.format && controls[schema.format]) return controls[schema.format]
  return controls[schemaType(schema) ?? '']
}

function complete(schema: Schema, node: Node | undefined, controls: Controls): boolean {
  if (schema['x-hidden']) return true
  const control = controlFor(schema, controls)
  if (schema.enum && !schema.enum.some((option) => option === valueOf(node))) return false
  if (control?.complete) return control.complete(valueOf(node))
  if (control) return valueOf(node) !== undefined
  return match(schemaType(schema))
    .with('object', () =>
      properties(schema).every(([key, child]) => {
        const member = at(node, [key])
        if (!schema.required?.includes(key) && valueOf(member) === undefined) return true
        return complete(child, member, controls)
      })
    )
    .with(
      'array',
      () =>
        node?.kind == 'array' && node.items.every((item) => complete(schema.items!, item, controls))
    )
    .otherwise(() => false)
}

function headingField(schema: Schema, controls: Controls) {
  return (
    properties(schema).find(
      ([key, child]) => controlFor(child, controls)?.itemLabel && schema.required?.includes(key)
    ) ??
    properties(schema).find(
      ([key, child]) =>
        key == 'name' && schema.required?.includes(key) && schemaType(child) == 'string'
    )
  )
}
function heading(schema: Schema, node: Node, fallback: string, controls: Controls): string {
  const field = headingField(schema, controls)
  if (!field) return fallback
  const value = valueOf(at(node, [field[0]]))
  const control = controlFor(field[1], controls)
  if (control?.itemLabel) return control.itemLabel(value) || fallback
  if (typeof value == 'string' && value.trim()) return value
  return fallback
}

export function SchemaEditor({
  schema,
  state,
  onEdit,
  controls = {}
}: {
  schema: Schema
  state: EditorState
  onEdit: (edit: Edit) => void
  controls?: Controls
}) {
  const context: Context = {
    state,
    send: onEdit,
    controls: { ...standardControls, ...controls },
    scope: 'document'
  }
  return (
    <Value
      schema={schema}
      node={state.root}
      path={[]}
      name={labelFor(schema, 'Document')}
      context={context}
    />
  )
}

function Value({
  schema,
  node,
  path,
  name,
  context,
  compact = false,
  omitKey
}: {
  schema: Schema
  node: Node | undefined
  path: Address
  name: string
  context: Context
  compact?: boolean
  omitKey?: string
}) {
  if (schema['x-hidden']) return null
  // Create the parent first. Its collections become available after Add.
  if (context.scope == 'creation' && schemaType(schema) == 'array') return null
  const control = controlFor(schema, context.controls)
  if (control) {
    const label = labelFor(schema, name)
    return (
      <div
        className={match(compact)
          .with(true, () => '')
          .otherwise(() => 'border-t border-gray-200 px-5 py-4')}
      >
        <label className="-mx-3 -my-2 block min-w-0 rounded-lg px-3 py-2 focus-within:bg-blue-100 focus-within:ring-1 focus-within:ring-blue-500">
          <span className="mb-1 block text-base font-normal text-gray-600">{label}</span>
          {control.render({
            schema,
            value: valueOf(node),
            label,
            onChange: (value) => context.send({ type: 'change', scope: context.scope, path, value })
          })}
        </label>
      </div>
    )
  }
  return match(schemaType(schema))
    .with('object', () => (
      <>
        {properties(schema)
          .filter(([key]) => key != omitKey)
          .map(([key, child]) => (
            <Value
              key={key}
              schema={child}
              node={at(node, [key])}
              name={key}
              path={[...path, key]}
              context={context}
              compact={compact}
            />
          ))}
      </>
    ))
    .with('array', () => (
      <Collection schema={schema} node={node} name={name} path={path} context={context} />
    ))
    .otherwise(() => {
      throw new Error(`Unsupported schema type: ${schemaType(schema)}`)
    })
}

function RemoveButton({
  title,
  className = '',
  onClick
}: {
  title: string
  className?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={`Remove ${title}`}
      className={`flex size-8 items-center justify-center rounded-lg p-0 text-gray-600 hover:bg-red-100 hover:text-red-700 focus-visible:outline focus-visible:outline-2 ${className}`}
      onClick={onClick}
    >
      <TrashIcon className="size-4 shrink-0" />
    </button>
  )
}
function Collection({
  schema,
  node,
  name,
  path,
  context
}: {
  schema: Schema
  node: Node | undefined
  name: string
  path: Address
  context: Context
}) {
  if (node && node.kind != 'array') throw new Error('Array schema requires array data')
  const items = node?.items ?? []
  const item = schema.items!
  const itemTitle = labelFor(item, item.source_type ?? 'Item')
  const addLabel = `Add ${itemTitle.toLowerCase()}`
  // A section heading is chosen on creation. Changing it requires replacing
  // the item; the remaining fields stay editable in the section body.
  const identity = headingField(item, context.controls)
  const cards =
    Boolean(controlFor(item, context.controls)) ||
    (schemaType(item) == 'object' &&
      properties(item).length > 0 &&
      properties(item).every(([, child]) => Boolean(controlFor(child, context.controls))))
  const skin = match(cards)
    .with(true, () => ({
      container: 'border-t border-gray-200 p-5',
      draft: 'mb-4 rounded-lg border border-gray-200 bg-gray-100 p-5',
      draftTitle: 'mb-5 text-base font-medium text-gray-900',
      draftBody: 'space-y-5',
      draftActions: 'mt-5 flex justify-end gap-2',
      add: ''
    }))
    .otherwise(() => ({
      container: '',
      draft: 'border-b border-gray-200 bg-gray-100',
      draftTitle: 'bg-white px-5 py-3 text-base font-medium text-gray-900',
      draftBody: '',
      draftActions: 'flex justify-end gap-2 p-5',
      add: 'p-5'
    }))
  const creation = context.state.creation
  const ownsCreation =
    context.scope == 'document' &&
    creation != null &&
    JSON.stringify(creation.parent) == JSON.stringify(path)
  const container = useRef<HTMLDivElement>(null)
  const draftId = useRef<number | null>(null)
  useEffect(() => {
    if (ownsCreation && creation) {
      // Keystrokes replace the draft tree; focus only when its identity changes.
      if (draftId.current != creation.root.id) {
        const draft = container.current?.querySelector<HTMLElement>('[data-schema-draft]')
        draft?.scrollIntoView({ block: 'nearest' })
        draft?.querySelector<HTMLElement>('input, select, button')?.focus({ preventScroll: true })
      }
      draftId.current = creation.root.id
    } else if (draftId.current != null) {
      // Cancel has no matching item. A committed item is expanded by default.
      container.current
        ?.querySelector(`[data-schema-item="${draftId.current}"]`)
        ?.scrollIntoView({ block: 'nearest' })
      draftId.current = null
    }
  }, [ownsCreation, creation])
  const remove = (id: number) => context.send({ type: 'remove', path, id })
  return (
    <div ref={container} className={skin.container}>
      {cards && (
        <div className="-mx-5 -mt-5 bg-gray-100 px-5 py-6 text-sm font-medium uppercase tracking-widest text-gray-600">
          {labelFor(schema, name)}
        </div>
      )}
      {items.map((entry, index) => {
        const itemPath = [...path, entry.id]
        const title = heading(item, entry, `${itemTitle} ${index + 1}`, context.controls)
        if (cards) {
          return (
            <div
              key={entry.id}
              data-schema-item={entry.id}
              className="group relative mb-4 rounded-lg border border-gray-200 bg-gray-100 p-5"
            >
              {/* Field spacing lives on an inner wrapper so the absolutely
                  positioned remove button does not receive a sibling margin. */}
              <div className="space-y-5 [&>*:first-child]:pr-8">
                <Value
                  compact
                  schema={item}
                  node={entry}
                  path={itemPath}
                  name={itemTitle}
                  context={context}
                />
              </div>
              <RemoveButton
                title={title}
                className="absolute right-3 top-3 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                onClick={() => remove(entry.id)}
              />
            </div>
          )
        }
        const collapsed = context.state.collapsed.includes(entry.id)
        return (
          <section
            key={entry.id}
            data-schema-item={entry.id}
            className="border-b border-gray-200 bg-gray-100"
          >
            <div className="group/section sticky top-0 z-20 flex h-12 items-center bg-white text-gray-900 shadow-sm">
              <button
                type="button"
                className="flex h-full min-w-0 flex-1 items-center px-5 text-left text-base font-medium uppercase tracking-wide hover:bg-gray-100"
                aria-expanded={!collapsed}
                onClick={() => context.send({ type: 'toggle', id: entry.id })}
              >
                <span className="truncate">{title}</span>
              </button>
              <div className="flex items-center opacity-0 group-focus-within/section:opacity-100 group-hover/section:opacity-100 [@media(hover:none)]:opacity-100">
                <RemoveButton title={title} onClick={() => remove(entry.id)} />
              </div>
              <button
                type="button"
                aria-label={`Toggle ${title}`}
                aria-expanded={!collapsed}
                className="flex h-full items-center px-5"
                onClick={() => context.send({ type: 'toggle', id: entry.id })}
              >
                <CaretDownIcon
                  className={match(collapsed)
                    .with(true, () => 'size-4 text-gray-600')
                    .otherwise(() => 'size-4 rotate-180 text-gray-600')}
                />
              </button>
            </div>
            {!collapsed && (
              <Value
                schema={item}
                node={entry}
                path={itemPath}
                name={itemTitle}
                context={context}
                omitKey={identity?.[0]}
              />
            )}
          </section>
        )
      })}
      {ownsCreation && creation && (
        <div data-schema-draft className={skin.draft}>
          <h3 className={skin.draftTitle}>{addLabel}</h3>
          <div className={skin.draftBody}>
            <Value
              compact={cards}
              schema={item}
              node={creation.root}
              path={[]}
              name={itemTitle}
              context={{ ...context, scope: 'creation' }}
            />
          </div>
          <div className={skin.draftActions}>
            <Button variant="default" onClick={() => context.send({ type: 'cancel' })}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!complete(item, creation.root, context.controls)}
              onClick={() => context.send({ type: 'commit' })}
            >
              {addLabel}
            </Button>
          </div>
        </div>
      )}
      {!ownsCreation && (
        <div className={skin.add}>
          <Button
            variant="default"
            className="h-12 w-full gap-2 rounded-lg text-lg font-semibold"
            disabled={context.scope == 'document' && creation != null}
            onClick={() => context.send({ type: 'add', path, value: initialValue(item) })}
          >
            <PlusIcon className="size-4" />
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  )
}
